/**
 * CashDetail — detail view for a cash account.
 * Sub-tabs: Overview · Transactions · Statements · Trends.
 * Only "Overview" is fully designed in Figma; the others are placeholders.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import DetailHeader from './DetailHeader';
import SubTabs from './SubTabs';
import { formatMoney } from '@/lib/format';
import { ACCOUNTS_TONE } from '@/lib/accountsDemo';
import { supabase } from '@/lib/supabaseClient';
import { prettifyDescription } from '@/lib/txDescription';
import CashTransactionsTab from './CashTransactionsTab';
import CashStatementsTab   from './CashStatementsTab';
import CashTrendsTab       from './CashTrendsTab';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function monthName(d) {
  return d.toLocaleDateString(undefined, { month: 'long' });
}

export default function CashDetail({ account, onBack, baseCurrency = 'INR', onEdit, onDelete, onSetBalance }) {
  // An individual account is ALWAYS shown in its own currency. FX conversion
  // belongs to aggregate figures only (net worth, group subtotals) — labelling
  // this account's ₹285,000 balance as "AED 285,000" because the user's base
  // happens to be AED is simply a wrong number.
  const nativeCurrency = account.currency || baseCurrency;
  const [tab, setTab] = useState('overview');
  const [txns, setTxns] = useState([]);
  const [txnsLoading, setTxnsLoading] = useState(true);
  const [aggregates, setAggregates] = useState({ moneyIn: 0, moneyOut: 0 });
  const tabs = [
    { id: 'overview',     label: 'Overview' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'statements',   label: 'Statements' },
    { id: 'trends',       label: 'Trends' },
  ];

  // Load recent transactions + this month's aggregates for this cash account.
  useEffect(() => {
    let cancelled = false;
    setTxnsLoading(true);
    (async () => {
      try {
        // Recent 6 transactions
        // Phase 82.9 — authoritative column is `txn_date` (schema.sql).
        // The prior `date` alias didn't exist on the transactions table, so
        // this query silently returned zero rows and the Cash detail view
        // never showed statement-imported activity.
        const { data: recent } = await supabase
          .from('transactions')
          .select('id, txn_date, description, amount, category, type')
          .eq('account_id', account.id)
          .order('txn_date', { ascending: false })
          .limit(6);
        // This month's totals
        const now = new Date();
        const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        const { data: monthRows } = await supabase
          .from('transactions')
          .select('amount, type')
          .eq('account_id', account.id)
          .gte('txn_date', monthStart);
        if (cancelled) return;
        setTxns(recent || []);
        const agg = (monthRows || []).reduce((acc, r) => {
          const n = Number(r.amount || 0);
          if (r.type === 'income' || n > 0) acc.moneyIn  += Math.abs(n);
          else                              acc.moneyOut -= Math.abs(n);
          return acc;
        }, { moneyIn: 0, moneyOut: 0 });
        setAggregates(agg);
      } catch (e) {
        console.warn('[CashDetail] transactions load failed:', e);
      } finally {
        if (!cancelled) setTxnsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [account?.id]);

  const { moneyIn, moneyOut } = aggregates;
  const net = moneyIn + moneyOut;

  // Phase 86 — "₹0 this month" is ambiguous: it can mean genuine inactivity OR
  // that nothing has been imported yet. Those need different words, so we say
  // which one it is rather than letting a zero imply a quiet month.
  const now = new Date();
  const latestTxnDate = txns[0]?.txn_date || null;
  const noActivityThisMonth = moneyIn === 0 && moneyOut === 0;
  const balanceAsOf = account.raw?.balance_as_of_date || null;
  const balanceStale = !!(balanceAsOf && latestTxnDate
    && String(latestTxnDate).slice(0, 10) > String(balanceAsOf).slice(0, 10));

  return (
    <div data-testid="accounts-cash-detail">
      <DetailHeader
        account={account}
        onBack={onBack}
        balance={formatMoney(Math.abs(account.balance || 0), nativeCurrency)}
        balanceLabel="Balance"
        onEdit={onEdit}
        onDelete={onDelete}
        onSetBalance={onSetBalance}
      />
      <SubTabs tabs={tabs} active={tab} onChange={setTab} testidPrefix="accounts-cash-tab" />

      {tab === 'overview' && (
        <div className="space-y-5">
          {/* Data-coverage disclosure — always above the numbers it qualifies. */}
          {(balanceStale || (noActivityThisMonth && !txnsLoading)) && (
            <div
              className="rounded-xl border px-4 py-3 flex items-start gap-2.5"
              style={{ borderColor: `${ACCOUNTS_TONE.warning}40`, backgroundColor: `${ACCOUNTS_TONE.warning}0F` }}
              data-testid="cash-detail-coverage-warning"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: ACCOUNTS_TONE.warning }} aria-hidden="true" />
              <div className="text-xs leading-relaxed">
                {balanceStale && (
                  <p className="text-charcoal">
                    <span className="font-semibold">Balance may be out of date.</span>{' '}
                    Transactions exist after the last reported balance
                    ({fmtDate(latestTxnDate)} vs {fmtDate(balanceAsOf)}).
                  </p>
                )}
                {noActivityThisMonth && !txnsLoading && (
                  <p className="text-charcoal">
                    No {monthName(now)} activity has been imported for this account.
                    {latestTxnDate ? ` Latest transaction: ${fmtDate(latestTxnDate)}.` : ''}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: `Money in · ${monthName(now)}`,  value: `+${formatMoney(moneyIn, nativeCurrency)}`,  color: ACCOUNTS_TONE.positive },
              { label: `Money out · ${monthName(now)}`, value: `−${formatMoney(Math.abs(moneyOut), nativeCurrency)}`, color: '#1E1E1E' },
              { label: `Net · ${monthName(now)}`,       value: `${net >= 0 ? '+' : '−'}${formatMoney(Math.abs(net), nativeCurrency)}`, color: net >= 0 ? ACCOUNTS_TONE.positive : ACCOUNTS_TONE.liability },
            ].map((m) => (
              <div key={m.label} className="bg-card rounded-xl border border-border px-4 py-4 shadow-sm">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">{m.label}</p>
                <p className="num font-display text-xl font-medium" style={{ color: m.color }}>{m.value}</p>
              </div>
            ))}
          </div>

          <section className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
            <header className="px-5 py-3.5 border-b border-border/60 bg-offwhite/60">
              <p className="text-xs font-semibold text-charcoal">Recent Transactions</p>
            </header>
            {txnsLoading ? (
              <div className="px-5 py-8 text-center text-xs text-muted-foreground">Loading transactions&hellip;</div>
            ) : txns.length === 0 ? (
              <div className="px-5 py-8 text-center text-xs text-muted-foreground">No transactions yet for this account.</div>
            ) : (
              <ul className="divide-y divide-border/40">
                {txns.map((t) => {
                  const amt = Number(t.amount || 0);
                  const positive = t.type === 'income' || amt > 0;
                  // Phase 86 — show the normalised merchant, not the raw bank
                  // string. The original description stays available in the
                  // transaction's own detail view.
                  const label = prettifyDescription(t.description)?.display
                    || t.description || 'Transaction';
                  return (
                    <li key={t.id} className="flex items-center gap-4 px-5 py-3.5">
                      <div className="w-8 h-8 rounded-lg bg-offwhite flex items-center justify-center text-[11px] font-semibold text-muted-foreground shrink-0">
                        {label.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-charcoal truncate" title={t.description || ''}>{label}</p>
                        <p className="text-[11px] text-muted-foreground">{t.category || '—'} · {fmtDate(t.txn_date)}</p>
                      </div>
                      <p
                        className="num text-sm font-semibold shrink-0"
                        style={{ color: positive ? ACCOUNTS_TONE.positive : '#1E1E1E' }}
                      >
                        {positive ? '+' : '−'}{formatMoney(Math.abs(amt), nativeCurrency)}
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      )}

      {tab === 'transactions' && <CashTransactionsTab account={account} baseCurrency={nativeCurrency} />}
      {tab === 'statements'   && <CashStatementsTab   account={account} />}
      {tab === 'trends'       && <CashTrendsTab       account={account} baseCurrency={baseCurrency} />}
    </div>
  );
}
